/**
 * IMPORT PAGE
 * 
 * Two-step import process:
 * 1. CSV Import - Import collection data from spreadsheet
 * 2. Photo Import - Match and upload photos to your items
 * 
 * Features:
 * - Drag & drop file upload
 * - Support for all item types (polish, plates, transfers, etc.)
 * - Auto-detect column mappings
 * - Manual column mapping adjustment
 * - Preview data before import
 * - Progress tracking
 * - Error handling and validation
 * - Photo matching wizard
 * - Photo URL detection and auto-import from Airtable, Google Sheets, etc.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Check, X, AlertCircle,
  Download, RefreshCw, HelpCircle, Loader2, CheckCircle2,
  Camera, FolderOpen, Sparkles, Image, AlertTriangle, Database, HardDrive
} from 'lucide-react';
import { collection, addDoc, Timestamp, getDocs, query, where, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import {
  parseCSV,
  autoMapColumns,
  applyMapping,
  validateMappedData,
  getUnmappedRequired,
  generateTemplate,
  getFieldsForType,
  ITEM_TYPE_INFO,
  type ParsedCSV,
  type ColumnMapping,
  type ImportItemType,
  type MappedRow
} from '../utils/csvUtils';
import {
  analyzeCSV,
  isImageUrl,
  type AnalysisResult
} from '../utils/csvAnalyzer';
import PhotoImportWizard from '../components/PhotoImportWizard';
import SmartPhotoMatcher from '../components/SmartPhotoMatcher';
import AirtableImport from '../components/AirtableImport';
import BackupImport from '../components/BackupImport';
import PreImportReview from '../components/PreImportReview';
import { compressImageBlob } from '../utils/imageUtils';
import { normalizeTypography } from '../utils/normalizeTypography';
import { useStorageUsage } from '../hooks/useStorageUsage';
import StorageUpgradeModal from '../components/StorageUpgradeModal';

type ImportMode = 'overview' | 'csv' | 'photos';
type ImportStep = 'upload' | 'analyze' | 'mapping' | 'preview' | 'review' | 'importing' | 'complete';

// Helper to fetch and upload a photo from URL - WITH DMCA SOURCE TRACKING
async function fetchAndUploadPhotoFromUrl(
  userId: string,
  itemId: string,
  url: string,
  storageQuota?: { usedBytes: number; limitBytes: number; sessionBytes: number }
): Promise<{ success: boolean; error?: string; bytesUploaded?: number; skippedStorage?: boolean }> {
  try {
    // Fetch the image
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (!response.ok) {
      return { success: false, error: `HTTP ${response.status}` };
    }
    
    const contentType = response.headers.get('content-type');
    if (!contentType?.startsWith('image/')) {
      return { success: false, error: 'Not an image' };
    }
    
    const blob = await response.blob();
    
    // Compress the image (resize to max 1200px width)
    const compressedBlob = await compressImageBlob(blob, 1200, 0.8);
    
    // Check storage AFTER compression (actual size that will be stored)
    if (storageQuota) {
      const totalUsed = storageQuota.usedBytes + storageQuota.sessionBytes + compressedBlob.size;
      if (totalUsed > storageQuota.limitBytes) {
        return { success: false, skippedStorage: true, error: 'Storage limit exceeded' };
      }
    }
    
    // Upload to Firebase Storage
    const timestamp = Date.now();
    const filename = `photo_${timestamp}.jpg`;
    const storageRef = ref(storage, `users/${userId}/items/${itemId}/${filename}`);
    
    const snapshot = await uploadBytes(storageRef, compressedBlob, {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000, immutable',
    });

    const downloadUrl = await getDownloadURL(snapshot.ref);

    // Update the item document with the photo - WITH DMCA TRACKING
    const itemRef = doc(db, 'users', userId, 'items', itemId);
    await updateDoc(itemRef, {
      photos: arrayUnion({
        url: downloadUrl,
        isDefault: false,
        // DMCA Source Tracking
        uploadedBy: userId,
        uploadedAt: new Date().toISOString(),
        source: 'import',           // Imported from CSV/Airtable
        sourceUrl: url,             // Original URL we fetched from
        dmcaStatus: 'active',
      })
    });
    
    return { success: true, bytesUploaded: compressedBlob.size };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}


export default function ImportPage() {
  
  
  // Mode selection (overview vs CSV vs photos)
  const [importMode, setImportMode] = useState<ImportMode>('overview');
  
  // Item type selection for import
  const [selectedItemType, setSelectedItemType] = useState<ImportItemType>('polish');
  
  // Step management for CSV import
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  
  // File & parsing state
  const [_file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedCSV | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  
  // Analysis state
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [_enabledTransforms, setEnabledTransforms] = useState<Set<string>>(new Set());
  
  // Mapping state
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  
  // Preview state
  const [validationResult, setValidationResult] = useState<ReturnType<typeof validateMappedData> | null>(null);
  const [_mappedRows, setMappedRows] = useState<any[]>([]);
  
  // Photo import option
  const [importPhotosFromUrls, setImportPhotosFromUrls] = useState(true);
  
  // Import state
  const [importProgress, setImportProgress] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [photoImportCount, setPhotoImportCount] = useState(0);
  const [photoImportErrors, setPhotoImportErrors] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [currentImportStatus, setCurrentImportStatus] = useState<string>('');
  
  // Drag & drop state
  const [isDragging, setIsDragging] = useState(false);
  
  // Photo import state
  const [showPhotoWizard, setShowPhotoWizard] = useState(false);
  const [showSmartMatcher, setShowSmartMatcher] = useState(false);
  const [polishesForPhotos, setPolishesForPhotos] = useState<any[]>([]);
  
  // Airtable import state
  const [showAirtableImport, setShowAirtableImport] = useState(false);
  const [showBackupImport, setShowBackupImport] = useState(false);
  const [loadingPolishes, setLoadingPolishes] = useState(false);
  
  // Storage tracking for photo uploads
  const { usedBytes, limitBytes, nextTier } = useStorageUsage();
  const [photosSkippedStorage, setPhotosSkippedStorage] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Redirect after import
  const navigate = useNavigate();
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  
  // Get fields for the selected item type
  const currentFields = getFieldsForType(selectedItemType);
  
  // Count polishes without photos
  const polishesWithoutPhotos = polishesForPhotos.filter(p => !p.photos || p.photos.length === 0).length;
  
  // Get unmapped required fields
  const unmappedRequired = getUnmappedRequired(columnMapping, selectedItemType);
  
  // Check if there are photo URLs to import
  const hasPhotoUrls = analysisResult?.photoUrlCount && analysisResult.photoUrlCount > 0;
  const photoUrlColumn = analysisResult?.photoUrlColumn;
  const photoUrlCount = analysisResult?.photoUrlCount || 0;

  // Auto-redirect to collection after import completes
  useEffect(() => {
    if (currentStep === 'complete' && redirectCountdown === null) {
      // Start 5 second countdown
      setRedirectCountdown(5);
    }
  }, [currentStep, redirectCountdown]);

  useEffect(() => {
    if (redirectCountdown === null || redirectCountdown < 0) return;
    
    if (redirectCountdown === 0) {
      navigate(`/collection?type=${selectedItemType}`);
      return;
    }
    
    const timer = setTimeout(() => {
      setRedirectCountdown(prev => (prev !== null ? prev - 1 : null));
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [redirectCountdown, navigate, selectedItemType]);

  // Load polishes for photo matching
  const loadPolishesForPhotos = useCallback(async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;
    
    setLoadingPolishes(true);
    try {
      const polishQuery = query(
        collection(db, 'users', currentUser.uid, 'items'),
        where('itemType', '==', 'polish')
      );
      const snapshot = await getDocs(polishQuery);
      const polishes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPolishesForPhotos(polishes);
    } catch (error) {
      console.error('Failed to load polishes:', error);
    } finally {
      setLoadingPolishes(false);
    }
  }, []);

  // Photo upload function for the wizard
  const uploadPhotoToFirebase = async (polishId: string, file: File): Promise<string> => {
    const currentUser = getCurrentUser();
    if (!currentUser) throw new Error('Not logged in');
    
    const timestamp = Date.now();
    const filename = `photo_${timestamp}.jpg`;
    const storageRef = ref(storage, `users/${currentUser.uid}/polishes/${polishId}/${filename}`);
    
    // Upload to Storage
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000, immutable',
    });
    
    // Get the download URL
    const downloadUrl = await getDownloadURL(snapshot.ref);
    
    // Update the polish document with the photo
    const polishRef = doc(db, 'users', currentUser.uid, 'items', polishId);
    await updateDoc(polishRef, {
      photos: arrayUnion({
        url: downloadUrl,
        uploadedAt: new Date().toISOString(),
        source: 'bulk_import'
      })
    });
    
    return downloadUrl;
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    setParseError(null);
    
    try {
      const parsed = await parseCSV(selectedFile);
      
      if (parsed.headers.length === 0) {
        setParseError('The file appears to be empty');
        return;
      }
      
      if (parsed.rowCount === 0) {
        setParseError('No data rows found in the file');
        return;
      }
      
      setParsedData(parsed);
      
      // Analyze the data
      const analysis = analyzeCSV(parsed.headers, parsed.rows);
      setAnalysisResult(analysis);
      
      // Enable all suggestions by default
      const defaultEnabled = new Set(analysis.suggestions.map(s => s.id));
      setEnabledTransforms(defaultEnabled);
      
      // Move to analyze step
      setCurrentStep('analyze');
    } catch (error) {
      console.error('Parse error:', error);
      setParseError('Failed to parse the file. Please make sure it\'s a valid CSV file.');
    }
  }, []);

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.txt'))) {
      handleFileSelect(droppedFile);
    } else {
      setParseError('Please upload a CSV file');
    }
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  }, [handleFileSelect]);

  // Update column mapping
  const updateMapping = (csvColumn: string, field: string | null) => {
    setColumnMapping(prev => ({ ...prev, [csvColumn]: field }));
  };


  // Proceed from analyze to mapping
  const handleProceedToMapping = () => {
    if (!parsedData) return;
    
    // Auto-detect column mappings based on analysis and selected item type
    const autoMapping = autoMapColumns(parsedData.headers, selectedItemType);
    setColumnMapping(autoMapping);
    setCurrentStep('mapping');
  };

// Proceed to preview (rename to handleProceedToPreview or keep as is)
  const handleProceedToPreview = () => {
    if (!parsedData) return;

    const mapped = applyMapping(parsedData.headers, parsedData.rows, columnMapping);
    setMappedRows(mapped);

    const validation = validateMappedData(mapped);
    setValidationResult(validation);

    setCurrentStep('preview');
  };

// Handle import with photo URL fetching
  const handleImport = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setImportError('Please log in to import items');
      return;
    }
    
    if (!validationResult) return;
    
    setCurrentStep('importing');
    setImportProgress(0);
    setImportedCount(0);
    setSkippedCount(0);
    setPhotoImportCount(0);
    setPhotoImportErrors(0);
    setImportError(null);
    setCurrentImportStatus('');
    
    const itemsToImport = validationResult.valid;
    const totalItems = itemsToImport.length;
    
    // Calculate total steps (items + photo imports if enabled)
    const itemsWithPhotoUrls = importPhotosFromUrls 
      ? itemsToImport.filter(row => row.photoUrl && isImageUrl(row.photoUrl)).length
      : 0;
    const totalSteps = totalItems + itemsWithPhotoUrls;
    let completedSteps = 0;
    
    try {
      // Check for existing items to avoid duplicates
      const existingQuery = query(
        collection(db, 'users', currentUser.uid, 'items'),
        where('itemType', '==', selectedItemType)
      );
      const existingDocs = await getDocs(existingQuery);
      const existingKeys = new Set(
        existingDocs.docs.map(doc => {
          const data = doc.data();
          return `${data.brand?.toLowerCase()}|${data.name?.toLowerCase()}`;
        })
      );
      
      let imported = 0;
      let skipped = 0;
      let photosImported = 0;
      let photoErrors = 0;
      
      // Track items with photo URLs for later processing
      const itemsWithPhotos: { itemId: string; photoUrl: string; brand: string; name: string }[] = [];
      
      // First pass: Import all items
      for (let i = 0; i < itemsToImport.length; i++) {
        const row = itemsToImport[i];
        const key = `${row.brand.toLowerCase()}|${row.name.toLowerCase()}`;
        
        setCurrentImportStatus(`Importing ${row.brand} - ${row.name}...`);
        
        // Skip if already exists in collection
        if (existingKeys.has(key)) {
          skipped++;
          setSkippedCount(skipped);
          completedSteps++;
          setImportProgress(Math.round((completedSteps / totalSteps) * 100));
          continue;
        }
        
        // Normalize smart-quote / typographic-dash variants down to plain
        // ASCII so a CSV exported from Excel (which loves smart quotes)
        // writes the same string as a name typed directly on Android.
        // Prevents the CSV path from re-introducing the duplicates the
        // backfill just cleaned up.
        const normalizedBrand = normalizeTypography(row.brand ?? '');
        const normalizedName = normalizeTypography(row.name ?? '');

        // Build the base document
        const itemData: any = {
          itemType: selectedItemType,
          userId: currentUser.uid,
          brand: normalizedBrand,
          brandLower: normalizedBrand.toLowerCase(),
          name: normalizedName,
          nameLower: normalizedName.toLowerCase(),
          quantity: row.quantity || 1,
          dateAdded: Timestamp.now(),
          importedFrom: 'csv',
          importedAt: Timestamp.now(),
        };
        
        // Add common optional fields if present
        if (row.notes) itemData.notes = row.notes;
        if (row.publicNotes) itemData.publicNotes = row.publicNotes;
        if (row.tags?.length) itemData.tags = row.tags;
        if (row.isFavorite) itemData.isFavorite = true;
        if (row.price !== undefined) itemData.price = row.price;
        if (row.storage) itemData.storage = row.storage;
        
        // Store the photo URL as external reference (will be processed later)
        if (row.photoUrl) {
          itemData.externalPhotoUrl = row.photoUrl;
        }
        
        // Add type-specific fields based on selectedItemType
        switch (selectedItemType) {
          case 'polish':
            itemData.category = row.category || 'Nail Lacquer';
            if (row.colorDescription) itemData.colorDescription = row.colorDescription;
            if (row.line) itemData.line = row.line;
            if (row.swatchNumber) itemData.swatchNumber = row.swatchNumber;
            if (row.collection) itemData.collectionName = row.collection;
            if (row.releaseType) itemData.releaseType = row.releaseType;
            if (row.releaseYear) itemData.releaseYear = row.releaseYear;
            if (row.releaseSeason) itemData.releaseSeason = row.releaseSeason;
            if (row.eventSource) {
              itemData.eventName = row.eventSource;
              itemData.eventSource = null;
            }
            if (row.eventTheme) itemData.eventTheme = row.eventTheme;
            if (row.colorFamily?.length) itemData.colorFamily = row.colorFamily;
            if (row.finish?.length) itemData.finish = row.finish;
            if (row.holoType?.length) itemData.holoType = row.holoType;
            if (row.particles?.length) itemData.particles = row.particles;
            if (row.specialFeatures?.length) itemData.specialFeatures = row.specialFeatures;
            if (row.utility?.length) itemData.utility = row.utility;
            if (row.colorCharacter?.length) itemData.colorCharacter = row.colorCharacter;
            if (row.baseType) itemData.baseType = row.baseType;
            if (row.colorShift) itemData.colorShift = row.colorShift;
            if (row.barcode) itemData.barcode = row.barcode;
            if (row.onWishlist) itemData.onWishlist = true;
            if (row.isRareLE) itemData.isRareLE = true;
            if (row.isForDestash) itemData.isForDestash = true;
            if (row.destashPrice !== undefined) itemData.destashPrice = row.destashPrice;
            if (row.destashCondition) itemData.destashCondition = row.destashCondition;
            if (row.swatched) itemData.swatched = true;
            if (row.timesUsed) itemData.timesUsed = row.timesUsed;
            if (row.lastUsedDate) itemData.lastUsedDate = row.lastUsedDate;
            // Release sub-fields
            if (row.boxName) itemData.boxName = row.boxName;
            if (row.boxTheme) itemData.boxTheme = row.boxTheme;
            if (row.groupName) itemData.groupName = row.groupName;
            if (row.groupTheme) itemData.groupTheme = row.groupTheme;
            if (row.exclusiveTo) itemData.exclusiveTo = row.exclusiveTo;
            if (row.exclusiveTheme) itemData.exclusiveTheme = row.exclusiveTheme;
            if (row.charityName) itemData.charityName = row.charityName;
            if (row.charityCause) itemData.charityCause = row.charityCause;
            if (row.conventionName) itemData.conventionName = row.conventionName;
            if (row.collabWith) itemData.collabWith = row.collabWith;
            if (row.collabTheme) itemData.collabTheme = row.collabTheme;
            if (row.polishType) itemData.polishType = row.polishType;
            break;
            
          case 'plate':
            if (row.plateType) itemData.plateType = row.plateType;
            if (row.plateSize) itemData.plateSize = row.plateSize;
            if (row.plateShape) itemData.plateShape = row.plateShape;
            if (row.imageCount) {
              const count = parseInt(row.imageCount);
              if (!isNaN(count)) itemData.imageCount = count;
            }
            if (row.collection) itemData.collectionName = row.collection;
            break;
            
          case 'transfer':
            if (row.transferType) itemData.transferType = row.transferType;
            if (row.collection) itemData.collectionName = row.collection;
            break;
            
          case 'embellishment':
            if (row.embellishmentType) itemData.embellishmentType = row.embellishmentType;
            if (row.color) itemData.color = row.color;
            if (row.size) itemData.size = row.size;
            break;
            
          case 'tool':
            if (row.toolType) itemData.toolType = row.toolType;
            if (row.material) itemData.material = row.material;
            break;
            
          case 'powder':
            if (row.powderType) itemData.powderType = row.powderType;
            if (row.color) itemData.color = row.color;
            if (row.effect) itemData.effect = row.effect;
            break;
            
          case 'acrylic':
            if (row.acrylicType) itemData.acrylicType = row.acrylicType;
            if (row.color) itemData.color = row.color;
            break;
        }

        // Add to Firestore
        const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'items'), itemData);
        
        // Track for photo import if URL exists and is an image
        if (importPhotosFromUrls && row.photoUrl && isImageUrl(row.photoUrl)) {
          itemsWithPhotos.push({
            itemId: docRef.id,
            photoUrl: row.photoUrl,
            brand: row.brand,
            name: row.name
          });
        }
        
        imported++;
        existingKeys.add(key); // Prevent duplicates within same import
        setImportedCount(imported);
        completedSteps++;
        setImportProgress(Math.round((completedSteps / totalSteps) * 100));
      }
      
      // Second pass: Fetch and upload photos from URLs
      if (importPhotosFromUrls && itemsWithPhotos.length > 0) {
        setCurrentImportStatus('Downloading and uploading photos...');
        let sessionBytes = 0;   // Track bytes uploaded this import session
        let storageSkipped = 0; // Track photos skipped due to storage limit
        
        for (let i = 0; i < itemsWithPhotos.length; i++) {
          const item = itemsWithPhotos[i];
          setCurrentImportStatus(`📸 Importing photo for ${item.brand} - ${item.name}... (${i + 1}/${itemsWithPhotos.length})`);
          
          const result = await fetchAndUploadPhotoFromUrl(
            currentUser.uid,
            item.itemId,
            item.photoUrl,
            { usedBytes, limitBytes, sessionBytes }
          );
          
          if (result.success) {
            photosImported++;
            setPhotoImportCount(photosImported);
            if (result.bytesUploaded) {
              sessionBytes += result.bytesUploaded;
            }
          } else if (result.skippedStorage) {
            storageSkipped++;
            setPhotosSkippedStorage(storageSkipped);
          } else {
            photoErrors++;
            setPhotoImportErrors(photoErrors);
            console.warn(`Failed to import photo for ${item.brand} - ${item.name}: ${result.error}`);
          }
          
          completedSteps++;
          setImportProgress(Math.round((completedSteps / totalSteps) * 100));
          
          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      setCurrentImportStatus('');
      setCurrentStep('complete');
    } catch (error) {
      console.error('Import error:', error);
      setImportError('An error occurred during import. Some items may have been imported.');
      setCurrentStep('complete');
    }
  };

  // Download template
  const handleDownloadTemplate = () => {
    const template = generateTemplate(selectedItemType);
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedItemType}-import-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Reset and start over
  const handleStartOver = () => {
    setCurrentStep('upload');
    setFile(null);
    setParsedData(null);
    setParseError(null);
    setAnalysisResult(null);
    setColumnMapping({});
    setValidationResult(null);
    setMappedRows([]);
    setImportProgress(0);
    setImportedCount(0);
    setSkippedCount(0);
    setPhotoImportCount(0);
    setPhotoImportErrors(0);
    setImportError(null);
    setImportPhotosFromUrls(true);
    setRedirectCountdown(null);
    setPhotosSkippedStorage(0);
  };

  // Go back to overview
  const handleBackToOverview = () => {
    handleStartOver();
    setImportMode('overview');
  };

  // Start photo import
  const handleStartPhotoImport = () => {
    loadPolishesForPhotos();
    setImportMode('photos');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* ============ OVERVIEW MODE ============ */}
      {importMode === 'overview' && (
        <>
          <div className="glass-card p-6 mb-6">
            <h1 className="text-3xl font-bold text-glass font-heading mb-2">
              📥 Import Your Collection
            </h1>
            <p className="text-glass-secondary font-body">
              Bring your existing collection data into Polish ALL the Things
            </p>
          </div>

          {/* Data Guide Tip */}
          <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 border border-purple-400 rounded-xl p-4 mb-6 shadow-lg">
            <div className="flex items-start gap-3">
              <HelpCircle className="icon-md text-white flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-white mb-1 font-body">
                  💡 New to importing?
                </p>
                <p className="text-sm text-white/90 font-body">
                  Check out our{' '}
                  <Link
                    to="/help/data-guide"
                    className="text-yellow-200 hover:text-yellow-100 underline font-medium"
                  >
                    Data Best Practices Guide
                  </Link>
                  {' '}to learn how to organize your collection for the best experience.
                </p>
              </div>
            </div>
          </div>

          {/* Two-step process */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Step 1: CSV Import */}
            <button
              onClick={() => setImportMode('csv')}
              className="glass-card p-6 text-left hover:bg-slate-800/70 transition-default group"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0 group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-default">
                  <FileSpreadsheet className="icon-lg text-glass" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-glass mb-1 font-body">
                    Step 1: Import Data
                  </h2>
                  <p className="text-glass-secondary text-sm mb-3 font-body">
                    Import your collection from a CSV or spreadsheet file. Works with exports from Google Sheets, Excel, Airtable, and more.
                  </p>
                  <div className="flex items-center gap-2 text-status-accent font-bold text-sm">
                    Get Started
                    <ArrowRight className="icon-sm" />
                  </div>
                </div>
              </div>
            </button>
            
            {/* Step 2: Photo Import */}
            <button
              onClick={handleStartPhotoImport}
              className="glass-card p-6 text-left hover:bg-slate-800/70 transition-default group"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0 group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-default">
                  <Camera className="icon-lg text-glass" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-glass mb-1 font-body">
                    Step 2: Add Photos
                  </h2>
                  <p className="text-glass-secondary text-sm mb-3 font-body">
                    Upload swatch photos and match them to your collection. Smart matching helps you bulk-add photos quickly.
                  </p>
                  <div className="flex items-center gap-2 text-status-accent font-bold text-sm">
                    Add Photos
                    <ArrowRight className="icon-sm" />
                  </div>
                </div>
              </div>
            </button>
          </div>
          
          {/* Alternative: Direct Airtable Import */}
          <div className="mb-8">
            <div className="text-center mb-4">
              <span className="text-sm text-glass-muted font-body px-4 py-1 bg-slate-800/50 rounded-full">
                or import directly from
              </span>
            </div>
            <button
              onClick={() => setShowAirtableImport(true)}
              className="glass-card p-6 w-full text-left hover:bg-slate-800/70 transition-default group border-2 border-yellow-500/30 hover:border-yellow-500/50"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0 group-hover:from-yellow-500/30 group-hover:to-orange-500/30 transition-default">
                  <Database className="icon-lg text-yellow-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-glass mb-1 font-body">
                    Import from Airtable
                    <span className="ml-2 text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded-full">
                      Recommended
                    </span>
                  </h2>
                  <p className="text-glass-secondary text-sm mb-3 font-body">
                    Connect directly to your Airtable base. Import data AND photos in one step with automatic linked record resolution.
                  </p>
                  <div className="flex items-center gap-2 text-yellow-400 font-bold text-sm">
                    Connect to Airtable
                    <ArrowRight className="icon-sm" />
                  </div>
                </div>
              </div>
            </button>
          </div>
          
          {/* Restore from a backup */}
          <div className="mb-8">
            <button
              onClick={() => setShowBackupImport(true)}
              className="glass-card p-6 w-full text-left hover:bg-slate-800/70 transition-default group border-2 border-purple-500/30 hover:border-purple-500/50"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:from-purple-500/30 group-hover:to-blue-500/30 transition-default">
                  <HardDrive className="icon-lg text-purple-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-glass mb-1 font-body">Restore from a backup</h2>
                  <p className="text-glass-secondary text-sm mb-3 font-body">
                    Re-import a Polish ALL the Things export (.zip or its data.json) — restores your
                    items and re-uploads their photos. Duplicates are skipped.
                  </p>
                  <div className="flex items-center gap-2 text-purple-300 font-bold text-sm">
                    Upload a backup
                    <ArrowRight className="icon-sm" />
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Quick tip */}
          <div className="mt-6 text-center">
            <p className="text-sm text-glass-secondary font-body">
              Already have items in your collection? You can go straight to{' '}
              <button
                onClick={handleStartPhotoImport}
                className="text-yellow-300 hover:text-yellow-200 hover:underline font-bold"
              >
                photo import
              </button>
              .
            </p>
          </div>
        </>
      )}

      {/* ============ CSV IMPORT MODE ============ */}
      {importMode === 'csv' && (
        <>
          {/* Header */}
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center gap-4 mb-2">
              <button
                onClick={handleBackToOverview}
                className="p-2 -ml-2 hover:bg-slate-800/60 rounded-lg transition-default"
              >
                <ArrowLeft className="icon-md text-glass-muted" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-glass font-heading">
                  {ITEM_TYPE_INFO[selectedItemType].emoji} Import {ITEM_TYPE_INFO[selectedItemType].label}
                </h1>
                <p className="text-glass-secondary font-body">
                  Step 1: Import your {ITEM_TYPE_INFO[selectedItemType].label.toLowerCase()} from a spreadsheet
                </p>
              </div>
            </div>
            
            {/* Progress steps */}
            <div className="flex items-center gap-2 mt-6 overflow-x-auto pb-2">
              {(['upload', 'analyze', 'mapping', 'preview', 'review', 'complete'] as const).map((step, index) => (
                <React.Fragment key={step}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-default whitespace-nowrap font-body ${
                    currentStep === step
                      ? 'progress-step-active'
                      : currentStep === 'importing' && step !== 'complete'
                        ? 'progress-step-pending'
                        : index < ['upload', 'analyze', 'mapping', 'preview', 'review', 'complete'].indexOf(currentStep)
                          ? 'progress-step-complete'
                          : 'progress-step-pending'
                  }`}>
                    {index < ['upload', 'analyze', 'mapping', 'preview', 'review', 'complete'].indexOf(currentStep) ? (
                      <Check className="icon-sm" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                    <span className="hidden sm:inline">
                      {step === 'upload' ? 'Upload' : step === 'analyze' ? 'Analyze' : step === 'mapping' ? 'Map Columns' : step === 'preview' ? 'Preview' : step === 'review' ? 'Review' : 'Done'}
                    </span>
                  </div>
                  {index < 5 && <ArrowRight className="icon-sm text-gray-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="glass-card p-6">
            
            {/* STEP 1: Upload */}
            {currentStep === 'upload' && (
              <div className="space-y-6">
                {/* Item Type Selector */}
                <div className="bg-slate-800/60 rounded-xl p-6">
                  <h3 className="font-bold text-glass mb-3 font-body">
                    What are you importing?
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {(Object.entries(ITEM_TYPE_INFO) as [ImportItemType, typeof ITEM_TYPE_INFO[ImportItemType]][]).map(([type, info]) => (
                      <button
                        key={type}
                        onClick={() => setSelectedItemType(type)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selectedItemType === type
                            ? 'border-slate-400 bg-slate-700/50'
                            : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/30'
                        }`}
                      >
                        <div className="text-2xl mb-1">{info.emoji}</div>
                        <div className="font-semibold text-glass text-sm font-body">{info.label}</div>
                        <div className="text-xs text-glass-muted mt-1 font-body">{info.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Drop zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-12 text-center transition-default ${
                    isDragging 
                      ? 'border-slate-600 bg-slate-700/50' 
                      : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/60'
                  }`}
                >
                  <FileSpreadsheet className={`w-16 h-16 mx-auto mb-4 ${isDragging ? 'text-glass' : 'text-glass-muted'}`} />
                  <p className="text-lg font-medium text-glass-secondary mb-2 font-body">
                    Drag & drop your {ITEM_TYPE_INFO[selectedItemType].label} CSV here
                  </p>
                  <p className="text-glass-muted mb-4">or</p>
                  <label className="inline-block px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold cursor-pointer hover:bg-slate-800 transition-default font-body">
                    <Upload className="icon-md inline mr-2" />
                    Choose File
                    <input
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleFileInput}
                      className="hidden"
                    />
                  </label>
                  <p className="text-sm text-glass-muted mt-4">Supports .csv and .txt files</p>
                </div>
                
                {/* Error message */}
                {parseError && (
                  <div className="alert-glass-red flex items-center gap-3">
                    <AlertCircle className="icon-md flex-shrink-0" />
                    <p>{parseError}</p>
                  </div>
                )}
                
                {/* Help section */}
                <div className="bg-slate-800/60 rounded-xl p-6 space-y-4">
                  <h3 className="font-bold text-glass flex items-center gap-2 font-body">
                    <HelpCircle className="icon-md text-glass" />
                    Tips for importing {ITEM_TYPE_INFO[selectedItemType].label.toLowerCase()}
                  </h3>
                  <ul className="space-y-2 text-glass-secondary text-sm font-body">
                    <li className="flex items-start gap-2">
                      <Check className="icon-sm text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Your file should have column headers in the first row</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="icon-sm text-green-500 mt-0.5 flex-shrink-0" />
                      <span>At minimum, include "Brand" and "Name" columns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="icon-sm text-green-500 mt-0.5 flex-shrink-0" />
                      <span>We'll auto-detect common column names for {ITEM_TYPE_INFO[selectedItemType].label.toLowerCase()}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="icon-sm text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Export from Google Sheets or Excel as CSV for best results</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="icon-sm text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Photo URLs?</strong> We can automatically download and import them!</span>
                    </li>
                  </ul>
                  
                  <button
                    onClick={handleDownloadTemplate}
                    className="flex items-center gap-2 text-glass-secondary hover:text-glass font-medium text-sm font-body"
                  >
                    <Download className="icon-sm" />
                    Download {ITEM_TYPE_INFO[selectedItemType].label.toLowerCase()} template
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: Analyze Data */}
            {currentStep === 'analyze' && parsedData && analysisResult && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-glass mb-2 font-body">
                    📊 Data Analysis
                  </h2>
                  <p className="text-glass-secondary font-body">
                    We analyzed your file and found <strong>{parsedData.headers.length}</strong> columns and <strong>{parsedData.rowCount}</strong> rows.
                    Review what we detected below.
                  </p>
                </div>
                
                {/* Photo URL Detection Alert */}
                {hasPhotoUrls && (
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center flex-shrink-0">
                        <Image className="icon-md text-purple-300" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-glass mb-1 font-body">
                          📸 Photo URLs Detected!
                        </h3>
                        <p className="text-sm text-glass-secondary mb-2 font-body">
                          We found <strong className="text-purple-300">{photoUrlCount}</strong> image URLs in the "{photoUrlColumn}" column.
                          We can automatically download and import these photos during the import process!
                        </p>
                        <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 rounded-lg p-2 mt-2">
                          <AlertTriangle className="icon-sm flex-shrink-0 mt-0.5" />
                          <span>
                            <strong>Airtable users:</strong> Photo URLs from Airtable expire in 2-24 hours. 
                            Import immediately after exporting for best results!
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Column Analysis Cards */}
                <div className="space-y-3">
                  <h3 className="font-bold text-glass-secondary font-body">
                    Column Analysis
                  </h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    {analysisResult.columns.map((col) => (
                      <div key={col.columnName} className="bg-slate-800/60 rounded-xl p-4 border border-slate-600">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-glass font-body">
                            {col.columnName}
                          </h4>
                          {col.suggestedField && (
                            <span className="text-xs px-2 py-1 bg-slate-700 text-glass rounded-full">
                              → {col.suggestedField}
                            </span>
                          )}
                        </div>
                        
                        {/* Sample values */}
                        <div className="text-xs text-glass-muted mb-2">
                          {col.sampleValues.slice(0, 2).map((v, i) => (
                            <span key={i} className="inline-block mr-2 truncate max-w-[150px]" title={v}>
                              "{v}"
                            </span>
                          ))}
                        </div>
                        
                        {/* Detection badges */}
                        <div className="flex flex-wrap gap-1">
                          {col.hasImageUrls && (
                            <span className="badge-glass-green">
                              📸 Photos ({col.imageUrlPercentage}%)
                            </span>
                          )}
                          {col.hasUrls && !col.hasImageUrls && (
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full">
                              🔗 URLs ({col.urlPercentage}%)
                            </span>
                          )}
                          {col.hasDate && (
                            <span className="badge-glass-blue">
                              📅 Dates ({col.datePercentage}%)
                            </span>
                          )}
                          {col.hasReleaseType && (
                            <span className="badge-glass-purple">
                              🏷️ Release Types ({col.releaseTypePercentage}%)
                            </span>
                          )}
                          {col.hasEventName && (
                            <span className="badge-glass-pink">
                              🎉 Events ({col.eventPercentage}%)
                            </span>
                          )}
                          {col.emptyCount > 0 && (
                            <span className="text-xs px-2 py-0.5 bg-slate-800/40 text-glass-secondary rounded-full">
                              {col.emptyCount} empty
                            </span>
                          )}
                        </div>
                        
                        {/* Detected values */}
                        {col.detectedReleaseTypes.length > 0 && (
                          <div className="mt-2 text-xs text-glass-secondary">
                            Found: {col.detectedReleaseTypes.join(', ')}
                          </div>
                        )}
                        {col.detectedEvents.length > 0 && (
                          <div className="mt-2 text-xs text-glass-secondary">
                            Events: {col.detectedEvents.join(', ')}
                          </div>
                        )}
                        {col.detectedUrlDomains.length > 0 && (
                          <div className="mt-2 text-xs text-glass-secondary">
                            Sources: {col.detectedUrlDomains.slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Navigation */}
                <div className="flex justify-between pt-4">
                  <button
                    onClick={handleBackToOverview}
                    className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                  >
                    <ArrowLeft className="icon-sm" />
                    Back
                  </button>
                  <button
                    onClick={handleProceedToMapping}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default font-body"
                  >
                    Continue to Mapping
                    <ArrowRight className="icon-sm" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Column Mapping */}
            {currentStep === 'mapping' && parsedData && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-glass mb-2 font-body">
                    🔗 Map Your Columns
                  </h2>
                  <p className="text-glass-secondary font-body">
                    We've auto-detected your columns. Adjust the mappings if needed.
                  </p>
                </div>
                
                {/* Required fields warning */}
                {unmappedRequired.length > 0 && (
                  <div className="alert-glass-amber flex items-center gap-3">
                    <AlertCircle className="icon-md flex-shrink-0" />
                    <p>Missing required mappings: <strong>{unmappedRequired.join(', ')}</strong></p>
                  </div>
                )}
                
                {/* Mapping grid */}
                <div className="space-y-3">
                  {parsedData.headers.map(header => (
                    <div key={header} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-slate-800/60 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-glass font-body">
                          {header}
                        </p>
                        <p className="text-xs text-glass-muted truncate">
                          Sample: {(parsedData.rows[0] as Record<string, any>)?.[header] || '(empty)'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4">
                        <ArrowRight className="icon-sm text-glass-muted flex-shrink-0 hidden sm:block" />
                        <span className="text-xs text-glass-muted sm:hidden">Maps to:</span>
                        <select
                          value={columnMapping[header] || ''}
                          onChange={(e) => updateMapping(header, e.target.value || null)}
                          className="select-glass flex-1 sm:flex-none sm:min-w-[200px] font-body"
                        >
                          <option value="">-- Skip this column --</option>
                          {Object.entries(currentFields).map(([key, field]) => (
                            <option key={key} value={key}>
                              {field.label} {field.required ? '*' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Navigation */}
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setCurrentStep('analyze')}
                    className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                  >
                    <ArrowLeft className="icon-sm" />
                    Back to Analysis
                  </button>
                  <button
                    onClick={handleProceedToPreview}
                    disabled={unmappedRequired.length > 0}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default disabled:opacity-50 disabled:cursor-not-allowed font-body"
                  >
                    Preview Import
                    <ArrowRight className="icon-sm" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Preview */}
            {currentStep === 'preview' && validationResult && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-glass mb-2 font-body">
                    👀 Preview Your Import
                  </h2>
                  <p className="text-glass-secondary font-body">
                    Review the data before importing.
                    <span className="text-status-success font-medium"> {validationResult.valid.length} ready</span>
                    {validationResult.invalid.length > 0 && (
                      <span className="text-status-error font-medium">, {validationResult.invalid.length} have errors</span>
                    )}
                    {validationResult.duplicates.length > 0 && (
                      <span className="text-status-warning font-medium">, {validationResult.duplicates.length} duplicates</span>
                    )}
                  </p>
                </div>
                
                {/* Photo Import Option */}
                {hasPhotoUrls && (
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importPhotosFromUrls}
                        onChange={(e) => setImportPhotosFromUrls(e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <span className="font-bold text-glass font-body">
                          📸 Import photos from URLs ({photoUrlCount} found)
                        </span>
                        <p className="text-sm text-glass-secondary mt-1 font-body">
                          Automatically download and upload photos to your collection. This may take a few minutes for large imports.
                        </p>
                        {importPhotosFromUrls && (
                          <div className="flex items-start gap-2 text-xs text-amber-300 mt-2">
                            <AlertTriangle className="icon-sm flex-shrink-0 mt-0.5" />
                            <span>
                              <strong>Airtable users:</strong> Import immediately! Photo URLs expire in 2-24 hours.
                            </span>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                )}
                
                {/* Preview table */}
                <div className="bg-slate-800/60 rounded-xl border border-slate-600 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800/60/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-bold text-glass-secondary font-body">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-glass-secondary font-body">Brand</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-glass-secondary font-body">Name</th>
                          <th className="px-4 py-3 text-left text-sm font-bold text-glass-secondary font-body">Release Info</th>
                          {hasPhotoUrls && (
                            <th className="px-4 py-3 text-left text-sm font-bold text-glass-secondary font-body">Photo</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {[...validationResult.valid.slice(0, 50), ...validationResult.invalid, ...validationResult.duplicates].map((row, index) => (
                          <tr key={index} className={`border-t border-slate-700 ${
                            row._errors.length > 0 ? 'bg-red-500/10' : ''
                          }`}>
                            <td className="px-4 py-2">
                              {row._errors.length > 0 ? (
                                <span className="inline-flex items-center gap-1 text-xs text-status-error" title={row._errors.join(', ')}>
                                  <X className="icon-sm" />
                                  {row._errors[0]}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-status-success">
                                  <Check className="icon-sm" />
                                  Ready
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-glass font-body">{row.brand || '-'}</td>
                            <td className="px-4 py-2 text-sm text-glass font-body">{row.name || '-'}</td>
                            <td className="px-4 py-2 text-sm text-glass-muted font-body">
                              {row.collection && (
                                <span className="block">📁 {row.collection}</span>
                              )}
                              {row.eventSource && (
                                <span className="block">
                                  🎉 {row.eventSource}
                                  {row.eventTheme && <span className="text-xs ml-1">- {row.eventTheme}</span>}
                                </span>
                              )}
                              {(row.releaseType || row.releaseYear) && (
                                <span className="block text-xs">
                                  {row.releaseType && <span className="badge-glass-purple mr-1">{row.releaseType}</span>}
                                  {row.releaseYear && <span>{row.releaseYear}</span>}
                                  {row.releaseSeason && <span className="ml-1">{row.releaseSeason}</span>}
                                </span>
                              )}
                              {!row.collection && !row.eventSource && !row.releaseType && !row.releaseYear && '-'}
                            </td>
                            {hasPhotoUrls && (
                              <td className="px-4 py-2 text-sm">
                                {row.photoUrl && isImageUrl(row.photoUrl) ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-purple-300">
                                    <Image className="icon-sm" />
                                    Yes
                                  </span>
                                ) : (
                                  <span className="text-xs text-glass-muted">-</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {validationResult.valid.length > 50 && (
                    <p className="px-4 py-2 text-sm text-glass-muted bg-slate-800/60/50 text-center">
                      Showing first 50 of {validationResult.valid.length} valid rows
                    </p>
                  )}
                </div>
                
                {/* Navigation */}
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setCurrentStep('mapping')}
                    className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                  >
                    <ArrowLeft className="icon-sm" />
                    Back to Mapping
                  </button>
                  <button
                    onClick={() => setCurrentStep('review')}
                    disabled={validationResult.valid.length === 0}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default disabled:opacity-50 disabled:cursor-not-allowed font-body"
                  >
                    Review & Import {validationResult.valid.length} Items
                    {importPhotosFromUrls && hasPhotoUrls && (
                      <span className="text-xs opacity-75">+ {photoUrlCount} photos</span>
                    )}
                    <ArrowRight className="icon-sm" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 5: Review Data Quality */}
            {currentStep === 'review' && validationResult && (
              <PreImportReview
                mappedRows={validationResult.valid}
                onProceed={(fixedRows) => {
                  // Update the validation result with fixed rows
                  setValidationResult({
                    ...validationResult,
                    valid: fixedRows as MappedRow[],
                  });
                  // Proceed to import
                  handleImport();
                }}
                onBack={() => setCurrentStep('preview')}
              />
            )}

            {/* STEP 6: Importing */}
            {currentStep === 'importing' && (
              <div className="py-12 text-center space-y-6">
                <Loader2 className="w-16 h-16 text-glass mx-auto animate-spin" />
                <div>
                  <h2 className="text-xl font-bold text-glass mb-2 font-body">
                    Importing Your Collection...
                  </h2>
                  <p className="text-glass-secondary font-body">
                    Please don't close this page
                  </p>
                  {currentImportStatus && (
                    <p className="text-sm text-purple-300 mt-2 font-body">
                      {currentImportStatus}
                    </p>
                  )}
                </div>
                
                {/* Progress bar */}
                <div className="max-w-md mx-auto">
                  <div className="flex justify-between text-sm text-glass-secondary mb-2">
                    <span>Progress</span>
                    <span>{importProgress}%</span>
                  </div>
                  <div className="progress-bar-track">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${importProgress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-status-success">{importedCount} imported</span>
                    {skippedCount > 0 && <span className="text-status-warning">{skippedCount} skipped</span>}
                    {photoImportCount > 0 && <span className="text-purple-300">📸 {photoImportCount} photos</span>}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 7: Complete */}
            {currentStep === 'complete' && (
              <div className="py-12 text-center space-y-6">
                {importError ? (
                  <AlertCircle className="w-16 h-16 text-amber-500 mx-auto" />
                ) : (
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
                )}
                
                <div>
                  <h2 className="text-xl font-bold text-glass mb-2 font-body">
                    {importError ? 'Import Completed with Warnings' : '🎉 Import Complete!'}
                  </h2>
                  {importError ? (
                    <p className="text-status-warning font-body">{importError}</p>
                  ) : (
                    <p className="text-glass-secondary font-body">
                      Successfully imported <strong className="text-status-success">{importedCount}</strong> {ITEM_TYPE_INFO[selectedItemType].label.toLowerCase()}
                      {skippedCount > 0 && (
                        <span> (<strong>{skippedCount}</strong> duplicates skipped)</span>
                      )}
                    </p>
                  )}
                  
                  {/* Photo import results */}
                  {(photoImportCount > 0 || photoImportErrors > 0) && (
                    <div className="mt-4 p-4 bg-purple-500/10 rounded-lg inline-block">
                      <p className="text-purple-300 font-body">
                        📸 <strong>{photoImportCount}</strong> photos imported
                        {photoImportErrors > 0 && (
                          <span className="text-amber-300"> ({photoImportErrors} failed - URLs may have expired)</span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {/* Storage limit warning */}
                  {photosSkippedStorage > 0 && (
                    <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl max-w-md mx-auto">
                      <div className="flex items-start gap-3 text-left">
                        <HardDrive className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-bold text-amber-300 font-body">
                            {photosSkippedStorage} photo{photosSkippedStorage !== 1 ? 's' : ''} skipped — storage full
                          </p>
                          <p className="text-sm text-amber-200/80 font-body mb-3">
                            Your items were imported successfully! Upgrade your plan to add the remaining photos.
                          </p>
                          {nextTier && (
                            <button
                              onClick={() => setShowUpgradeModal(true)}
                              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-default"
                            >
                              Upgrade Storage
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Redirect countdown notice */}
                {redirectCountdown !== null && redirectCountdown > 0 && (
                  <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-300 text-sm font-body">
                      Redirecting to your collection in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}...
                    </p>
                  </div>
                )}
                
                <div className="flex flex-col items-center gap-3 pt-4">
                  <Link
                    to={`/collection?type=${selectedItemType}`}
                    className="flex items-center justify-center gap-2 w-full max-w-xs px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default font-body"
                  >
                    View Your {ITEM_TYPE_INFO[selectedItemType].label} Now
                    <ArrowRight className="icon-sm" />
                  </Link>
                  
                  {/* Suggest photo import if no photos were imported */}
                  {photoImportCount === 0 && selectedItemType === 'polish' && (
                    <button
                      onClick={() => {
                        setRedirectCountdown(null);
                        handleStartPhotoImport();
                      }}
                      className="flex items-center justify-center gap-2 w-full max-w-xs px-6 py-3 border border-purple-500/50 text-purple-300 rounded-lg font-semibold hover:bg-purple-500/10 transition-default font-body"
                    >
                      <Camera className="icon-sm" />
                      Add Photos to Your Collection
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setRedirectCountdown(null);
                      handleStartOver();
                    }}
                    className="flex items-center justify-center gap-2 w-full max-w-xs px-6 py-3 text-glass-secondary hover:text-glass font-medium font-body"
                  >
                    <RefreshCw className="icon-sm" />
                    Import Another File
                  </button>
                  
                  {/* Stay on page button */}
                  {redirectCountdown !== null && redirectCountdown > 0 && (
                    <button
                      onClick={() => setRedirectCountdown(null)}
                      className="text-sm text-glass-muted hover:text-glass-secondary font-body"
                    >
                      Cancel redirect
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ============ PHOTO IMPORT MODE ============ */}
      {importMode === 'photos' && !showPhotoWizard && !showSmartMatcher && (
        <>
          {/* Header */}
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center gap-4 mb-2">
              <button
                onClick={handleBackToOverview}
                className="p-2 -ml-2 hover:bg-slate-800/60 rounded-lg transition-default"
              >
                <ArrowLeft className="icon-md text-glass-muted" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-glass font-heading">
                  📸 Import Photos
                </h1>
                <p className="text-glass-secondary font-body">
                  Step 2: Add swatch photos to your collection
                </p>
              </div>
            </div>
          </div>

          {/* Loading state */}
          {loadingPolishes ? (
            <div className="glass-card p-12 text-center">
              <Loader2 className="w-12 h-12 text-glass mx-auto animate-spin mb-4" />
              <p className="text-glass-secondary font-body">
                Loading your collection...
              </p>
            </div>
          ) : polishesForPhotos.length === 0 ? (
            /* No polishes yet */
            <div className="glass-card p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="icon-lg text-status-warning" />
              </div>
              <h2 className="text-xl font-bold text-glass mb-2 font-body">
                No Polishes Found
              </h2>
              <p className="text-glass-secondary mb-6 font-body">
                You need to import your collection data first before adding photos.
              </p>
              <button
                onClick={() => setImportMode('csv')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default font-body"
              >
                <FileSpreadsheet className="icon-md" />
                Import CSV First
              </button>
            </div>
          ) : (
            /* Ready for photo import */
            <div className="glass-card p-6">
              <div className="text-center mb-8">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-4">
                  <Camera className="icon-xl text-status-accent" />
                </div>
                <h2 className="text-xl font-bold text-glass mb-2 font-body">
                  Ready to Add Photos!
                </h2>
                <p className="text-glass-secondary font-body">
                  You have <strong>{polishesForPhotos.length}</strong> items in your collection
                  {polishesWithoutPhotos > 0 && (
                    <span>, <strong className="text-status-accent">{polishesWithoutPhotos}</strong> without photos</span>
                  )}
                </p>
              </div>
              
              {/* Two photo import options */}
              <div className="space-y-4">
                {/* Option 1: Smart Matcher (Recommended) */}
                <div className="p-4 border-2 border-purple-500/50 rounded-xl bg-purple-500/5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="icon-md text-status-accent" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-glass mb-1 font-body">
                        Smart Photo Matcher
                        <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/30 text-purple-300 rounded-full">Recommended</span>
                      </h3>
                      <p className="text-sm text-glass-secondary mb-3 font-body">
                        Upload photos with any naming format. We'll match by filename and let you resolve any conflicts manually.
                      </p>
                      <button
                        onClick={() => setShowSmartMatcher(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all font-body"
                      >
                        <Sparkles className="icon-sm" />
                        Open Smart Matcher
                      </button>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-slate-600"></div>
                  <span className="text-sm text-glass-muted font-body">or</span>
                  <div className="flex-1 h-px bg-slate-600"></div>
                </div>

                {/* Option 2: Folder-based Wizard */}
                <div className="p-4 border border-slate-600 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                      <FolderOpen className="icon-md text-glass-muted" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-glass mb-1 font-body">
                        Folder-Based Wizard
                      </h3>
                      <p className="text-sm text-glass-secondary mb-3 font-body">
                        Best for photos already organized by brand folders. Requires "Brand/PolishName.jpg" structure.
                      </p>
                      <button
                        onClick={() => setShowPhotoWizard(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-600 transition-all font-body"
                      >
                        <FolderOpen className="icon-sm" />
                        Open Folder Wizard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="mt-6 alert-glass-purple">
                <h4 className="alert-glass-purple-title mb-2 font-body">
                  💡 Photo Naming Tips
                </h4>
                <ul className="alert-glass-purple-text space-y-1 font-body">
                  <li>• <strong>Best:</strong> "Brand - Polish Name.jpg" (e.g., "ILNP - Eclipse.jpg")</li>
                  <li>• <strong>Good:</strong> "Polish Name.jpg" (works if name is unique)</li>
                  <li>• All formats supported: JPG, PNG, GIF, WebP, HEIC</li>
                  <li>• Photos are automatically compressed to save storage</li>
                </ul>
              </div>
            </div>
          )}
        </>
      )}

      {/* Photo Import Wizard Modal */}
      {showPhotoWizard && (
        <PhotoImportWizard
          polishes={polishesForPhotos}
          onImportComplete={() => {
            // Reload polishes to update photo counts
            loadPolishesForPhotos();
          }}
          onClose={() => setShowPhotoWizard(false)}
          uploadPhoto={uploadPhotoToFirebase}
        />
      )}

      {/* Smart Photo Matcher Modal */}
      {showSmartMatcher && (
        <SmartPhotoMatcher
          items={polishesForPhotos}
          userId={getCurrentUser()?.uid || ''}
          onClose={() => setShowSmartMatcher(false)}
          onComplete={() => {
            // Reload polishes to update photo counts
            loadPolishesForPhotos();
            setShowSmartMatcher(false);
          }}
        />
      )}

      {/* Airtable Import Modal */}
      {showAirtableImport && (
        <AirtableImport
          onComplete={() => {
            // Could navigate to collection or just close
            setShowAirtableImport(false);
          }}
          onClose={() => setShowAirtableImport(false)}
        />
      )}

      {showBackupImport && <BackupImport onClose={() => setShowBackupImport(false)} />}
      
      {/* Storage Upgrade Modal */}
      {showUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          highlightTier={nextTier?.id}
        />
      )}
    </div>
  );
}
